import type { AttributionData } from "./core/AttributionManager";
import type { FairshareConfig, TrackableEventWithData } from "./core/types";
import { FairShareCore } from "./core/FairShareCore";
import {
  DEFAULT_PATH_ATTRIBUTION_PATTERN,
  DEFAULT_SUBDOMAIN_ATTRIBUTION_PATTERN,
} from "./core/types";

// Singleton instance
let instance: FairShareCore | null = null;

/**
 * Get the FairShare instance
 * @returns The FairShare instance
 * @throws Error if FairShare is not initialized
 */
function getInstance(): FairShareCore {
  if (!instance) {
    try {
      instance = FairShareCore.getInstance();
    } catch (error) {
      // Re-throw with a more descriptive message
      console.error("FairShare error:", error);
      throw new Error(
        "FairShare not initialized. Call initializeFairshare first.",
      );
    }
  }
  return instance;
}

/**
 * Initialize the FairShare SDK
 * @param config Configuration options
 * @returns Promise that resolves when initialization is complete
 */
export function initializeFairshare(config: FairshareConfig): Promise<void> {
  // Reset any existing instance to ensure clean initialization
  reset();

  // Create and initialize the instance in one step
  instance = FairShareCore.createInstance(config);

  // Return the initialization Promise from FairShareCore
  return instance.getInitializationPromise();
}

/**
 * Track a page view
 * @param options Event options
 * @returns A promise that resolves when the event is tracked
 */
export function trackPageVisit(): void {
  return getInstance().trackPageVisit();
}

/**
 * Track a custom event
 * @param eventName Event name
 * @param properties Event properties
 * @param options Event options
 * @returns A promise that resolves when the event is tracked
 */
export function trackFairshareEvent(event: TrackableEventWithData): void {
  return getInstance().trackFairshareEvent(event);
}

/**
 * Identify a user
 * @param userId User ID
 * @param traits User traits
 */
export function identify(
  userId: string,
  traits?: Record<string, unknown>,
): void {
  getInstance().identify(userId, traits);
}

/**
 * Reset the SDK state
 */
export function reset(): void {
  if (instance) {
    instance.reset();
    instance = null;
  }
  // Reset the singleton instance in the core class
  FairShareCore.resetInstance();
}

export function getSessionToken(): string {
  try {
    const sessionToken = getInstance().getSessionToken();
    if (!sessionToken) {
      // More descriptive error message that explains the possible cause
      throw new Error(
        "Session token not found. Make sure initializeFairshare has been called and that initialization is complete before accessing the session token.",
      );
    }
    return sessionToken;
  } catch (error) {
    // Re-throw with the original message, but provide additional debug info
    console.error("Error retrieving session token:", error);
    throw error;
  }
}

export function getAttribution(): AttributionData | null {
  return getInstance().getStoredAttribution();
}

/**
 * Flush all pending events in the queue
 * @param sync Whether to wait for all events to complete processing
 * @param timeoutMs Maximum time to wait for events to complete (only applies when sync=true)
 * @returns Promise that resolves when events are processed
 */
export function flushEvents(sync = true, timeoutMs = 3000): Promise<void> {
  return getInstance().flushEvents(sync, timeoutMs);
}

// Export types
export type { FairshareConfig };

// Export default attribution patterns for external use
export {
  DEFAULT_PATH_ATTRIBUTION_PATTERN,
  DEFAULT_SUBDOMAIN_ATTRIBUTION_PATTERN,
};
